import { Directive, ElementRef, HostBinding, Input, AfterViewInit, OnDestroy } from '@angular/core';

/**
 * Agrega la clase `reveal-visible` al elemento la primera vez que entra en el viewport
 * (IntersectionObserver, no scroll listener — no corre en cada frame de scroll). El
 * elemento arranca con `reveal-on-scroll` desde el constructor, así que si el usuario
 * tiene JS deshabilitado o el observer no llega a dispararse por algún motivo, igual
 * conviene que el estado "oculto" en CSS nunca deje el contenido ilegible (ver ejemplo
 * de uso en home.component.scss: opacity baja + translateY, nunca display:none).
 *
 * El consumidor define en su propio .scss cómo se ve "oculto" vs "visible" — esta
 * directiva solo pone/saca la clase, no trae estilos propios (así cada componente decide
 * su propia animación de entrada sin heredar nada no deseado).
 *
 * Uso:
 *   <div appRevealOnScroll [revealDelay]="i * 120">...</div>
 */
@Directive({
  selector: '[appRevealOnScroll]'
})
export class RevealOnScrollDirective implements AfterViewInit, OnDestroy {
  /** Delay en ms antes de agregar la clase, para escalonar varios elementos (ver *ngFor con index). */
  @Input() revealDelay = 0;

  @HostBinding('class.reveal-on-scroll') readonly baseClass = true;
  @HostBinding('class.reveal-visible') isVisible = false;

  private observer?: IntersectionObserver;
  private delayTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.isVisible = true;
      return;
    }

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.delayTimeoutId = setTimeout(() => { this.isVisible = true; }, this.revealDelay);
        this.observer?.disconnect();
      }
    }, { threshold: 0.15 });

    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    clearTimeout(this.delayTimeoutId);
  }
}
